import type { ReactNode } from "react";
import { KitFocusable, KitTooltip, KitTooltipTrigger } from "../../components/kit/Tooltip";
import { cn } from "@/utils/helpers";
import type { CalendarPlatform } from "@/types/contentCalendar.type";
import PlatformGlyph from "../../components/PlatformGlyph";
import { TurnDot } from "../components/DateRail";
import type { TurnOwner } from "../postState";
import { CHANNEL_NAME } from "./calendarModel";

/** Channel marks in the calendar's fixed order: 13px glyphs 6px apart on a post card (Calendar / Post card). */
export function ChannelMarks({
  channels,
  className,
  size = "sm",
}: {
  channels: readonly CalendarPlatform[];
  className?: string;
  size?: "sm" | "md";
}) {
  if (channels.length === 0) return null;
  return (
    <span className={cn("flex shrink-0 items-center gap-1.5 text-co-muted", className)}>
      {channels.map((platform) => (
        <PlatformGlyph
          key={platform}
          platform={platform}
          className={size === "md" ? "size-4" : "size-[13px]"}
        />
      ))}
      <span className="sr-only">{channels.map((platform) => CHANNEL_NAME[platform]).join(", ")}</span>
    </span>
  );
}

/**
 * Your turn in the accent ink with a filled dot; the client's in muted ink
 * with a hollow one. A failure (late, Missed, Didn't post) takes the alert
 * ink: lime means "needs you", never "went wrong" (Emad, 13 Oct).
 */
export function turnInk(owner: TurnOwner, alert = false): string {
  if (alert || owner === "late") return "var(--co-alert-ink)";
  if (owner === "you") return "var(--co-accent-ink)";
  return "var(--co-muted)";
}

export function TurnLine({
  owner,
  label,
  alert = false,
  className,
  wrap = false,
}: {
  owner: TurnOwner;
  label: string;
  /** Missed / Didn't post: the alert ink. */
  alert?: boolean;
  className?: string;
  /** A narrow card (the tablet's month) lets the words wrap instead of cutting them. */
  wrap?: boolean;
}) {
  return (
    <span
      className={cn(
        "flex min-w-0 gap-1.5 text-[12px] leading-[1.3]",
        wrap ? "items-start" : "items-center",
        className,
      )}
      style={{ color: turnInk(owner, alert) }}
    >
      <TurnDot owner={alert ? "late" : owner} />
      <span className={cn("min-w-0", wrap ? "break-words" : "truncate")}>{label}</span>
    </span>
  );
}

/** A keyboard key. Monospace is kept for keys only (DESIGN.md "Type on the calendar"). */
export function Kbd({ children }: { children: ReactNode }) {
  return (
    <kbd className="inline-grid min-w-[18px] place-items-center border border-co-line2 px-1 font-mono text-[10.5px] leading-[16px] text-co-muted">
      {children}
    </kbd>
  );
}

/**
 * A label tooltip (library Tooltip / Label) on a react-aria trigger, with an
 * optional key cap.
 */
export function CalendarTip({
  label,
  keys,
  children,
  placement = "bottom",
  delay = 500,
}: {
  label: string;
  keys?: string[];
  children: ReactNode;
  placement?: "top" | "bottom" | "left" | "right" | "bottom start" | "bottom end";
  delay?: number;
}) {
  return (
    <KitTooltipTrigger delay={delay} closeDelay={100}>
      {children}
      <KitTooltip placement={placement} offset={6} shortcut={keys?.join(" ")}>
        {label}
      </KitTooltip>
    </KitTooltipTrigger>
  );
}

/** A plain element as a tooltip trigger. */
export { KitFocusable as CalendarTipTarget };
